/**
 * DocumentTypeFieldsDrawer — panel boczny „Pola typu” w Ustawienia → Typy dokumentów.
 *
 * Otwiera się po kliknięciu komórki „Pola typu” w wierszu typu i pokazuje efektywne pola
 * dodatkowe klasy dla pary (typ, firma): jeden wybór na pole spośród „Nie dotyczy · Opcjonalne ·
 * Wymagane”, grupy „Pola typu” i „Poza typem”, wyszukiwarkę i filtr „Tylko zmienione”.
 *
 * Zapis jest natychmiastowy (PUT stanu pola w nakładce firmy) z optymistyczną zmianą stanu i
 * cofnięciem pola przy błędzie (toast). Oznaczane są tylko odstępstwa od ustawienia ITE
 * (kropka + „ITE: …”, własne pola dołożone w firmie: „dołożone w tej firmie”); „↺” przywraca
 * jedno pole, „Przywróć ustawienie ITE” (z potwierdzeniem) cały typ w tej firmie. Pole będące
 * podstawą numeracji typu jest zawsze wymagane — wybór zablokowany z podpowiedzią. Bez prawa
 * zarządzania typami panel jest tylko do odczytu.
 *
 * Klawiatura: wybór stanu ma aktywację ręczną (strzałki przesuwają fokus, Spacja/Enter zapisują),
 * a po zmianie, która przenosi wiersz między grupami albo usuwa go z widoku, fokus wraca na to
 * pole (gdy go nie ma w widoku — na sąsiednie pole albo nagłówek grupy). Wyszukiwanie, filtr,
 * potwierdzenie i blokady dotyczą jednej pary typ/firma i czyszczą się przy otwarciu innej.
 *
 * Reguła „Nie dotyczy”: pole nie pokazuje się na karcie dokumentu tego typu i nie jest
 * sprawdzane jako wymagane; wartości nie są kasowane. Reguła liczona po stronie serwera
 * (`GET /document-types/:id/fields`).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  DocumentClass,
  DocumentTypeFieldsResponse,
  DocumentTypeFieldState,
  EffectiveDocumentTypeField,
} from '@saas/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { RotateCcw } from 'lucide-react';

import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FieldStateSegmented } from '@/components/documents/FieldStateSegmented';
import { Drawer } from '@/components/Drawer';
import { IconButton } from '@/components/IconButton';
import { SearchInput } from '@/components/SearchInput';
import { Spinner } from '@/components/Spinner';
import { useToast } from '@/contexts/ToastContext';
import {
  deleteDocumentTypeFieldOverride,
  DOCUMENT_TYPE_FIELDS_QUERY_PREFIX,
  documentTypeFieldsKey,
  documentTypeFieldsSummaryKey,
  fetchDocumentTypeFields,
  isDeviation,
  putDocumentTypeFieldState,
  resetDocumentTypeFields,
} from '@/lib/documents/document-type-fields';

type WriteVars = { slug: string; kind: 'set'; state: DocumentTypeFieldState } | WriteRestoreVars;
type WriteRestoreVars = { slug: string; kind: 'restore' };

interface WriteContext {
  previous: EffectiveDocumentTypeField | undefined;
}

export interface DocumentTypeFieldsDrawerProps {
  open: boolean;
  onClose: () => void;
  docClass: DocumentClass;
  /** `null` = panel zamknięty (nic nie pobieramy). */
  typeId: string | null;
  typeName: string;
  /** Firma nakładki; `null` tylko dla typu lokalnego bez firmy. */
  companyId: string | null;
  companyName: string;
  /** Prawo zarządzania typami klasy — bez niego panel jest tylko do odczytu. */
  canManage: boolean;
}

export function DocumentTypeFieldsDrawer({
  open,
  onClose,
  docClass,
  typeId,
  typeName,
  companyId,
  companyName,
  canManage,
}: DocumentTypeFieldsDrawerProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [onlyChanged, setOnlyChanged] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  // Pola z zapisem w toku — ich wybór i „↺” są zablokowane do końca zapisu, żeby szybkie kliknięcia
  // w to samo pole nie ścigały się (zapis i cofnięcie błędu opierają się na jednym poprzednim stanie).
  const [pendingSlugs, setPendingSlugs] = useState<ReadonlySet<string>>(new Set());

  function setPending(slug: string, pending: boolean): void {
    setPendingSlugs((prev) => {
      const next = new Set(prev);
      if (pending) next.add(slug);
      else next.delete(slug);
      return next;
    });
  }

  // Stan widoku (wyszukiwanie, filtr, potwierdzenie, blokady) należy do jednej pary typ/firma:
  // przy otwarciu panelu dla innej pary (albo ponownym otwarciu) zaczynamy od czystego widoku.
  // Zamknięcie panelu niczego nie czyści (zamknięty panel nie pokazuje treści) — stan zerujemy
  // przy kolejnym otwarciu.
  const [scopeKey, setScopeKey] = useState('');
  const currentScopeKey = open && typeId !== null ? `${typeId}:${companyId ?? ''}` : '';
  if (currentScopeKey !== scopeKey) {
    setScopeKey(currentScopeKey);
    if (currentScopeKey !== '') {
      setSearch('');
      setOnlyChanged(false);
      setConfirmReset(false);
      setPendingSlugs(new Set());
    }
  }

  // Powrót fokusu po zmianie stanu pola: wiersz może przejść do drugiej grupy albo zniknąć z widoku
  // (filtr „Tylko zmienione”, „↺”, cofnięcie po błędzie) — wtedy fokus idzie na sąsiedni wiersz,
  // a gdy grupa jest pusta, na jej nagłówek.
  const contentRef = useRef<HTMLDivElement | null>(null);
  const focusRequest = useRef<{
    slug: string;
    neighborSlug: string | null;
    group: 'in' | 'out';
  } | null>(null);
  const visibleRef = useRef<EffectiveDocumentTypeField[]>([]);

  function requestFocus(slug: string): void {
    const list = visibleRef.current;
    const field = list.find((f) => f.slug === slug);
    if (!field) {
      focusRequest.current = null;
      return;
    }
    const sameGroup = list.filter((f) => (f.state === 'excluded') === (field.state === 'excluded'));
    const at = sameGroup.findIndex((f) => f.slug === slug);
    const neighbor = sameGroup[at + 1] ?? sameGroup[at - 1] ?? null;
    focusRequest.current = {
      slug,
      neighborSlug: neighbor?.slug ?? null,
      group: field.state === 'excluded' ? 'out' : 'in',
    };
  }

  const queryKey = documentTypeFieldsKey(typeId ?? '', companyId);
  const writeKey = ['document-type-field-write', typeId, companyId] as const;

  const fieldsQ = useQuery({
    queryKey,
    queryFn: () => fetchDocumentTypeFields(typeId as string, companyId),
    enabled: open && typeId !== null,
    retry: false,
  });

  /** Odświeża podsumowanie w tabeli typów i karty dokumentów (inne pary typ/firma). */
  function invalidateDependents(): void {
    void queryClient.invalidateQueries({
      queryKey: documentTypeFieldsSummaryKey(docClass, null).slice(0, 3),
    });
    void queryClient.invalidateQueries({
      queryKey: [DOCUMENT_TYPE_FIELDS_QUERY_PREFIX],
      predicate: (q) => !(q.queryKey[1] === (typeId ?? '') && q.queryKey[2] === companyId),
    });
  }

  function patchField(slug: string, patch: Partial<EffectiveDocumentTypeField>): void {
    queryClient.setQueryData<DocumentTypeFieldsResponse>(queryKey, (current) =>
      current
        ? {
            ...current,
            fields: current.fields.map((f) => (f.slug === slug ? { ...f, ...patch } : f)),
          }
        : current,
    );
  }

  function errorTitle(err: unknown, fallbackKey: string): string {
    if (isAxiosError(err)) {
      const code = (err.response?.data as { error?: string } | undefined)?.error;
      if (code === 'FIELD_REQUIRED_BY_NUMBERING')
        return t('tenant.documentTypes.fields.toast.numbering');
      if (code === 'CONCURRENT_UPDATE') return t('tenant.documentTypes.fields.toast.concurrent');
      if (err.response?.status === 403) return t('tenant.documentTypes.fields.toast.forbidden');
    }
    return t(fallbackKey);
  }

  const writeMutation = useMutation<DocumentTypeFieldsResponse, unknown, WriteVars, WriteContext>({
    mutationKey: writeKey,
    mutationFn: (vars) =>
      vars.kind === 'set'
        ? putDocumentTypeFieldState(typeId as string, vars.slug, companyId, vars.state)
        : deleteDocumentTypeFieldOverride(typeId as string, vars.slug, companyId),
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey });
      const current = queryClient.getQueryData<DocumentTypeFieldsResponse>(queryKey);
      const previous = current?.fields.find((f) => f.slug === vars.slug);
      if (vars.kind === 'set') {
        patchField(vars.slug, { state: vars.state, overridden: true });
      } else if (previous) {
        patchField(vars.slug, { state: previous.baseState, overridden: false });
      }
      return { previous };
    },
    onError: (err, vars, context) => {
      if (context?.previous) {
        requestFocus(vars.slug);
        patchField(vars.slug, {
          state: context.previous.state,
          overridden: context.previous.overridden,
        });
      }
      toast.show({
        variant: 'error',
        title: errorTitle(
          err,
          vars.kind === 'set'
            ? 'tenant.documentTypes.fields.toast.saveError'
            : 'tenant.documentTypes.fields.toast.restoreError',
        ),
      });
    },
    onSettled: (_data, _err, vars) => {
      setPending(vars.slug, false);
      // Odświeżenie z serwera dopiero, gdy nie ma kolejnych zapisów w toku (nie nadpisujemy
      // optymistycznego stanu pola, które użytkownik właśnie zmienił).
      if (queryClient.isMutating({ mutationKey: writeKey }) <= 1) {
        void queryClient.invalidateQueries({ queryKey });
      }
      invalidateDependents();
    },
  });

  const resetMutation = useMutation<DocumentTypeFieldsResponse>({
    mutationFn: () => resetDocumentTypeFields(typeId as string, companyId),
    onSuccess: (data) => {
      queryClient.setQueryData<DocumentTypeFieldsResponse>(queryKey, data);
      invalidateDependents();
      toast.show({ variant: 'success', title: t('tenant.documentTypes.fields.toast.resetDone') });
      setConfirmReset(false);
    },
    onError: (err) => {
      toast.show({
        variant: 'error',
        title: errorTitle(err, 'tenant.documentTypes.fields.toast.resetError'),
      });
      setConfirmReset(false);
    },
  });

  const data = fieldsQ.data;
  const fields = useMemo(() => data?.fields ?? [], [data]);

  const stats = useMemo(
    () => ({
      inType: fields.filter((f) => f.state !== 'excluded').length,
      required: fields.filter((f) => f.state === 'required').length,
      deviations: fields.filter(isDeviation).length,
      anyOverride: fields.some((f) => f.overridden),
    }),
    [fields],
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return fields.filter(
      (f) => (!onlyChanged || isDeviation(f)) && (q === '' || f.label.toLowerCase().includes(q)),
    );
  }, [fields, search, onlyChanged]);
  const inType = visible.filter((f) => f.state !== 'excluded');
  const outOfType = visible.filter((f) => f.state === 'excluded');
  useEffect(() => {
    visibleRef.current = visible;
  });

  // Po każdej zmianie danych pól oddaj fokus zgłoszonemu polu (patrz `requestFocus`).
  useEffect(() => {
    const request = focusRequest.current;
    const root = contentRef.current;
    if (!request || !root) return;
    const radioOf = (slug: string | null): HTMLElement | null => {
      if (slug === null) return null;
      for (const row of root.querySelectorAll<HTMLElement>('[data-field-slug]')) {
        if (row.dataset.fieldSlug === slug) {
          return row.querySelector<HTMLElement>('[role="radio"][aria-checked="true"]');
        }
      }
      return null;
    };
    const target =
      radioOf(request.slug) ??
      radioOf(request.neighborSlug) ??
      root.querySelector<HTMLElement>(`[data-group="${request.group}"]`);
    focusRequest.current = null;
    target?.focus();
  }, [fields]);

  // Zapis w toku (któregokolwiek pola) albo reset całego typu blokują „Przywróć ustawienie ITE”;
  // reset z kolei wstrzymuje wybór stanów i „↺”.
  const anyWriting = pendingSlugs.size > 0;

  function choose(field: EffectiveDocumentTypeField, next: DocumentTypeFieldState): void {
    if (!canManage || field.requiredByNumbering || next === field.state) return;
    if (pendingSlugs.has(field.slug) || resetMutation.isPending) return;
    // Wybór stanu z ustawienia ITE = powrót do ustawienia (bez zbędnego wiersza nakładki).
    if (next === field.baseState) {
      if (field.overridden) startWrite({ slug: field.slug, kind: 'restore' });
      return;
    }
    startWrite({ slug: field.slug, kind: 'set', state: next });
  }

  function startWrite(vars: WriteVars): void {
    if (resetMutation.isPending) return;
    requestFocus(vars.slug);
    setPending(vars.slug, true);
    writeMutation.mutate(vars);
  }

  function iteHint(field: EffectiveDocumentTypeField): string {
    if (field.origin === 'own' && field.baseState === 'excluded') {
      return t('tenant.documentTypes.fields.addedHere');
    }
    return t('tenant.documentTypes.fields.iteState', {
      state: t(`tenant.documentTypes.fields.stateLower.${field.baseState}`),
    });
  }

  function renderRow(field: EffectiveDocumentTypeField) {
    const deviation = isDeviation(field);
    const pending = pendingSlugs.has(field.slug);
    // Trwała blokada (brak prawa, pole numeracji) wyszarza segmenty; zapis w toku i reset tylko
    // wstrzymują zmianę (`busy`).
    const locked = !canManage || field.requiredByNumbering;
    const busy = pending || resetMutation.isPending;
    return (
      <li
        key={field.slug}
        data-field-slug={field.slug}
        className="flex items-center gap-3 border-b border-border-secondary px-4 py-2"
        data-testid={`document-type-fields-row-${field.slug}`}
      >
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm text-text-primary">
            {field.label}
            {field.origin === 'own' && (
              <span className="ml-1.5 text-xs text-text-muted">
                · {t('tenant.documentTypes.fields.ownField')}
              </span>
            )}
          </div>
          {deviation && (
            <div
              className="flex items-center gap-1.5 text-xs text-[rgb(var(--color-warning-text))]"
              data-testid={`document-type-fields-deviation-${field.slug}`}
            >
              <span
                aria-hidden="true"
                className="inline-block h-2 w-2 rounded-full bg-[rgb(var(--color-warning))]"
              />
              {iteHint(field)}
            </div>
          )}
          {field.requiredByNumbering && (
            <div
              className="text-xs text-text-muted"
              data-testid={`document-type-fields-numbering-${field.slug}`}
            >
              {t('tenant.documentTypes.fields.requiredByNumbering')}
            </div>
          )}
        </div>
        <FieldStateSegmented
          value={field.state}
          onChange={(next) => choose(field, next)}
          label={t('tenant.documentTypes.fields.stateGroupAria', { name: field.label })}
          disabled={locked}
          busy={busy}
          activation="manual"
          testIdPrefix={`document-type-field-${field.slug}`}
        />
        {deviation && canManage ? (
          <IconButton
            size="sm"
            aria-label={t('tenant.documentTypes.fields.restoreField', { name: field.label })}
            title={t('tenant.documentTypes.fields.restoreFieldShort')}
            disabled={busy}
            onClick={() => startWrite({ slug: field.slug, kind: 'restore' })}
            data-testid={`document-type-field-restore-${field.slug}`}
          >
            <RotateCcw aria-hidden="true" />
          </IconButton>
        ) : (
          <span className="inline-block w-7 flex-none" aria-hidden="true" />
        )}
      </li>
    );
  }

  const hasBaseSetting = data?.hasBaseSetting ?? false;

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        width="sm"
        testId="document-type-fields-drawer"
        title={t('tenant.documentTypes.fields.title', { name: typeName })}
        // Do czasu wczytania pól nie wiadomo, czy typ ma ustawienie ITE — pokazujemy samą firmę.
        description={
          data
            ? t(
                hasBaseSetting
                  ? 'tenant.documentTypes.fields.subtitleWithSetting'
                  : 'tenant.documentTypes.fields.subtitleNoSetting',
                { company: companyName },
              )
            : companyName
        }
        footer={
          <div className="flex w-full items-center justify-between gap-3">
            {canManage ? (
              <Button
                variant="secondary"
                size="sm"
                disabled={!stats.anyOverride || resetMutation.isPending || anyWriting}
                onClick={() => setConfirmReset(true)}
                data-testid="document-type-fields-reset"
              >
                {t('tenant.documentTypes.fields.resetAll')}
              </Button>
            ) : (
              <span
                className="text-xs text-text-muted"
                data-testid="document-type-fields-readonly-hint"
              >
                {t('tenant.documentTypes.fields.readOnlyHint')}
              </span>
            )}
            {canManage && (
              <span className="text-xs text-text-muted">
                {t('tenant.documentTypes.fields.autosaveHint')}
              </span>
            )}
          </div>
        }
      >
        {fieldsQ.isLoading ? (
          <div className="flex items-center justify-center py-10">
            <Spinner />
          </div>
        ) : fieldsQ.isError || !data ? (
          <p
            className="text-sm text-[rgb(var(--color-error-text))]"
            data-testid="document-type-fields-error"
          >
            {t('tenant.documentTypes.fields.loadError')}
          </p>
        ) : (
          <div ref={contentRef} className="-mx-5 -my-4 flex flex-col">
            <div className="border-b border-border-secondary px-4 py-3">
              <p className="text-sm text-text-secondary" data-testid="document-type-fields-stats">
                {t('tenant.documentTypes.fields.stats', {
                  fields: t('tenant.documentTypes.fields.fieldCount', { count: stats.inType }),
                  required: t('tenant.documentTypes.fields.requiredCount', {
                    count: stats.required,
                  }),
                })}
                {stats.deviations > 0 && (
                  <span className="text-[rgb(var(--color-warning-text))]">
                    {' · '}
                    <span
                      aria-hidden="true"
                      className="mr-1 inline-block h-2 w-2 rounded-full bg-[rgb(var(--color-warning))]"
                    />
                    {t('tenant.documentTypes.fields.deviationCount', { count: stats.deviations })}
                  </span>
                )}
              </p>
              {!hasBaseSetting && (
                <p
                  className="mt-1 text-xs text-text-muted"
                  data-testid="document-type-fields-nobase"
                >
                  {t('tenant.documentTypes.fields.noBaseSetting')}
                </p>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <SearchInput
                  value={search}
                  onChange={setSearch}
                  placeholder={t('tenant.documentTypes.fields.searchPlaceholder')}
                  aria-label={t('tenant.documentTypes.fields.searchAria')}
                  className="min-w-[140px] flex-1"
                  data-testid="document-type-fields-search"
                />
                <Button
                  variant={onlyChanged ? 'tertiary' : 'secondary'}
                  size="sm"
                  aria-pressed={onlyChanged}
                  onClick={() => setOnlyChanged((v) => !v)}
                  data-testid="document-type-fields-filter-changed"
                >
                  {t('tenant.documentTypes.fields.filterChanged')}
                </Button>
              </div>
            </div>

            {fields.length === 0 ? (
              <p
                className="px-4 py-6 text-sm text-text-muted"
                data-testid="document-type-fields-none"
              >
                {t('tenant.documentTypes.fields.noFields')}
              </p>
            ) : visible.length === 0 ? (
              <p
                className="px-4 py-6 text-sm text-text-muted"
                data-testid="document-type-fields-nomatch"
              >
                {t('tenant.documentTypes.fields.noMatch')}
              </p>
            ) : (
              <>
                <h3
                  tabIndex={-1}
                  data-group="in"
                  className="border-b border-border-secondary bg-bg-secondary px-4 py-2 text-xs font-semibold text-text-secondary outline-none focus-visible:ring-2 focus-visible:ring-accent-500"
                  data-testid="document-type-fields-group-in"
                >
                  {t('tenant.documentTypes.fields.groupIn', { count: inType.length })}
                </h3>
                <ul>{inType.map(renderRow)}</ul>
                <h3
                  tabIndex={-1}
                  data-group="out"
                  className="border-b border-border-secondary bg-bg-secondary px-4 py-2 text-xs font-semibold text-text-secondary outline-none focus-visible:ring-2 focus-visible:ring-accent-500"
                  data-testid="document-type-fields-group-out"
                >
                  {t('tenant.documentTypes.fields.groupOut', { count: outOfType.length })}
                </h3>
                <ul>{outOfType.map(renderRow)}</ul>
              </>
            )}
          </div>
        )}
      </Drawer>

      <ConfirmDialog
        open={confirmReset}
        title={t('tenant.documentTypes.fields.resetConfirmTitle')}
        message={t('tenant.documentTypes.fields.resetConfirmMessage', {
          name: typeName,
          company: companyName,
        })}
        confirmLabel={t('tenant.documentTypes.fields.resetConfirmLabel')}
        variant="primary"
        confirming={resetMutation.isPending}
        onConfirm={() => resetMutation.mutate()}
        onClose={() => setConfirmReset(false)}
      />
    </>
  );
}
